// Deflect "Set up Digital Receipt" wizard schema. Same shape as Recover's
// Configure schema (SECTION_KEYS / labels / per-step field list) so the shared
// framework (LeftRail / SectionForm / QuestionField / Footer / validation /
// progress) drives it unchanged. The last step ('review') has no fields; the
// SectionForm renders the receipt-preview + summary for it instead.
//
// 5-step layout. Step 1 is kept clean (3 core fields) like Recover's step 1; the
// logo moves into step 2 with the rest of the business details.

import {
  validateArnCode,
  validateBinCode,
  validateCaidCode,
  validateDescriptor
} from "../ConfigureProfilePage/utils/codeValidators"
import { COUNTRY_CODES_WITHOUT_SUBDIVISIONS } from "./countries-without-subdivisions"
import { RECEIPT_CONFIGURE_SLUGS } from "./receipt-configure-slugs"

// Step 3 code fields, referred to by key outside the schema (fill, payload, status messages).
export const PAYMENT_IDENTIFIER_FIELD = Object.freeze({
  BILLING_DESCRIPTORS: "billing_descriptors",
  ARN_CODES: "arn_codes",
  BIN_CODES: "bin_codes",
  CAID_CODES: "caid_codes"
})

export const SECTION_KEYS = [
  RECEIPT_CONFIGURE_SLUGS.business_info,
  RECEIPT_CONFIGURE_SLUGS.business_details,
  RECEIPT_CONFIGURE_SLUGS.payment_identifiers,
  RECEIPT_CONFIGURE_SLUGS.support_legal,
  RECEIPT_CONFIGURE_SLUGS.preview
]

// Rail labels (left rail + Next/Previous buttons).
export const SECTION_LABELS = {
  [RECEIPT_CONFIGURE_SLUGS.business_info]: "Business info",
  [RECEIPT_CONFIGURE_SLUGS.business_details]: "Business details",
  [RECEIPT_CONFIGURE_SLUGS.payment_identifiers]: "Payment identifiers",
  [RECEIPT_CONFIGURE_SLUGS.support_legal]: "Support & legal",
  [RECEIPT_CONFIGURE_SLUGS.preview]: "Preview"
}

// Form headings (shown above each step's fields).
export const SECTION_TITLES = {
  [RECEIPT_CONFIGURE_SLUGS.business_info]: "Business info",
  [RECEIPT_CONFIGURE_SLUGS.business_details]: "Business details",
  [RECEIPT_CONFIGURE_SLUGS.payment_identifiers]: "Payment identifiers",
  [RECEIPT_CONFIGURE_SLUGS.support_legal]: "Support and legal",
  [RECEIPT_CONFIGURE_SLUGS.preview]: "Review and submit"
}

// Subtitle shown under each form heading. Step 1 stays clean (no subtitle).
export const SECTION_SUBTITLES = {
  [RECEIPT_CONFIGURE_SLUGS.business_info]: "",
  [RECEIPT_CONFIGURE_SLUGS.business_details]:
    "Add your logo, address, and what you sell. We use these when your processor's order data is missing details.",
  [RECEIPT_CONFIGURE_SLUGS.payment_identifiers]:
    "Add BINs and CAIDs. These identifiers are required to help match your transactions to the right order details.",
  [RECEIPT_CONFIGURE_SLUGS.support_legal]:
    "Add the support and policy details customers can refer to if they have questions about a charge.",
  [RECEIPT_CONFIGURE_SLUGS.preview]: "To make changes, select a step on the left."
}

export const SECTION_STEPS = Object.fromEntries(
  SECTION_KEYS.map((k, i) => [k, { stepNumber: i + 1, totalSteps: SECTION_KEYS.length }])
)

// Numbered category groups for the wizard UI (mirrors Recover's SECTION_GROUPS). Only
// the multi-category steps are grouped; the focused single-topic steps (business_info,
// identifiers) render their fields flat under the step heading. SectionForm skips any
// group whose questions are all hidden and renders any ungrouped question as a fallback.
// A `keys` entry that is a nested array packs its questions into one horizontal
// row (2 columns on desktop, collapses to single column on narrow viewports —
// see .sectionFormFieldRow in shell.module.less). `blockHelp` renders once at
// the end of the group as a full-width helper line — used for the address block
// per CHA1-1523 §11.8 so the copy sits under the whole block, not one field.
export const SECTION_GROUPS = {
  [RECEIPT_CONFIGURE_SLUGS.business_details]: [
    {
      title: "Branding & address",
      keys: [
        "business_logo",
        "business_address",
        ["city", "postal_code"],
        "country_code",
        "state_region_name"
      ],
      blockHelp: "This address helps banks and customers identify your business."
    },
    { title: "Product details", keys: ["product_description"] }
  ],
  [RECEIPT_CONFIGURE_SLUGS.support_legal]: [
    { title: "Support", keys: ["support_phone", "support_email", "support_website"] },
    {
      title: "Legal & policies",
      keys: ["terms_url", "refund_policy_url", "cancellation_policy_url"]
    }
  ]
}

const SECTION_SCHEMA = {
  [RECEIPT_CONFIGURE_SLUGS.business_info]: [
    {
      key: "business_name",
      type: "text",
      label: "Business name",
      required: true,
      max: 100,
      placeholder: "Example: Acme Coffee",
      help: "This name appears next to transactions in your customers' banking apps."
    },
    {
      key: "business_website",
      type: "url",
      label: "Business website",
      required: true,
      max: 100,
      placeholder: "Example: https://www.acmecoffee.com",
      help: "Enter your main customer-facing website."
    },
    // Mirrors Recover's business description field exactly (label, AI assist) so
    // the two tools stay consistent. aiAssist 'description' uses the same crawl +
    // suggest endpoint as Recover. max differs from Recover's (§11.7: 250, not 500).
    // Under 250, so 249 at most (CHA1-3759) — matches the backend serializers.
    {
      key: "business_description",
      type: "textarea",
      label: "Describe your business in 2-3 sentences",
      max: 249,
      required: true,
      aiAssist: "description"
    }
  ],
  [RECEIPT_CONFIGURE_SLUGS.business_details]: [
    {
      key: "business_logo",
      type: "upload",
      label: "Business logo",
      accept: "image/png",
      acceptHint:
        "Recommended: Square image, at least 320 x 320 px, PNG with a transparent or white background.",
      optional: true
    },
    // Address block (CHA1-1523 §11.8, per Roman's 07 Aug spec update): the
    // single "Business address" field is split into 6 fields, of which only
    // Business address is typed from scratch — the geo fields are IP-prefilled
    // on first open by the backend. `softMax` on address keeps a long paste
    // whole so Next surfaces "Maximum N characters" instead of the input
    // silently truncating it.
    {
      key: "business_address",
      type: "text",
      label: "Business address",
      required: true,
      max: 50,
      softMax: true,
      placeholder: "Example: 1420 Lakeshore Blvd"
    },
    {
      key: "city",
      type: "text",
      label: "City",
      required: true,
      max: 50,
      placeholder: "Example: Chicago"
    },
    {
      key: "postal_code",
      type: "text",
      label: "Postal code",
      required: true,
      max: 20,
      placeholder: "Example: 60611"
    },
    // Single source of truth is ISO2. The backend also stores the display
    // name in a `country` column — externalOps.flattenProfile derives it from
    // the countries list at save time so it doesn't need to live in wizard
    // state.
    {
      key: "country_code",
      type: "country",
      label: "Country",
      required: true,
      placeholder: "Select country"
    },
    // The State / Region code is never shown and not counted (CHA1-4846, §11.8):
    // the backend takes it from the visitor's IP. The hidden field only carries
    // the saved code through a save, and changing the country clears it
    // (configure.slice saveSection) so the backend takes the new country's code.
    {
      key: "state_region",
      type: "text",
      hidden: true
    },
    // What the merchant sees as State / Region is the region name, free text
    // (CHA1-4846): rendered, required and counted only for a country with ISO
    // 3166-2 subdivisions (CHA1-3722). It never changes the code above.
    {
      key: "state_region_name",
      type: "text",
      label: "State / Region",
      required: true,
      max: 50,
      placeholder: "Example: Illinois",
      requiredError: "This field is required for the selected country.",
      showIf: {
        not: {
          key: "business_details.country_code",
          oneOf: COUNTRY_CODES_WITHOUT_SUBDIVISIONS
        }
      }
    },
    {
      key: "product_description",
      type: "textarea",
      label: "Product or service description",
      required: true,
      max: 100,
      placeholder: "Example: Products ordered through the website",
      help: "Summarize what you sell without naming specific products."
    }
  ],
  [RECEIPT_CONFIGURE_SLUGS.payment_identifiers]: [
    {
      key: PAYMENT_IDENTIFIER_FIELD.BILLING_DESCRIPTORS,
      type: "text-multi",
      label: "Billing descriptor(s)",
      required: true,
      placeholder: "Separate descriptors with commas",
      addLabel: "+ Add descriptor",
      tooltip: "The text that appears on the cardholder's statement for your charges.",
      validateValue: validateDescriptor,
      // Editable only on first submission; read-only afterwards (§11.9).
      lockAfterFirstSubmit: true
    },
    // Step 3 default: ARN. Hidden once the merchant ticks "use BIN and CAID
    // instead" (identifiers.use_bin_caid), and also hidden (along with the
    // checkbox) once identifiers.both_sets is set by prefill (§11.9) - in that
    // state all three code blocks show together with no either/or choice.
    {
      key: PAYMENT_IDENTIFIER_FIELD.ARN_CODES,
      type: "text-multi",
      label: "ARN codes",
      required: true,
      placeholder: "Enter an ARN code",
      addLabel: "+ Add ARN",
      tooltip:
        "The Acquirer Reference Number for your Visa transactions. At least one 23-digit code, starting with 2 or 7.",
      validateValue: validateArnCode,
      uniqueItems: true,
      uniqueItemsError: "ARN codes must be different.",
      lockAfterFirstSubmit: true,
      showIf: {
        any: [
          { key: "payment_identifiers.use_bin_caid", notEquals: true },
          { key: "payment_identifiers.both_sets", equals: true }
        ]
      }
    },
    {
      key: "use_bin_caid",
      type: "checkbox",
      label: "I want to use BIN and CAID instead of ARNs.",
      // The helper's "Learn how to get your BIN and CAID" segment is a link in the
      // spec (§11.9), which does not give a destination. Rather than invent one, this
      // reuses the URL the platform already ships for the same question on the
      // onboarding descriptor form (src/Components/Forms/BillingDescriptorsFormConfigure.js).
      // The article covers BIN/CAID for RDR, i.e. the same Visa codes this step
      // collects (§9.5 / §11.9 label them VISA ARN/BIN/CAID).
      helpText: "Select this if you don't have any ARN codes.",
      helpLinkText: "Learn how to get your BIN and CAID",
      helpLinkHref:
        "https://help.chargeback.io/en/articles/12782785-how-to-get-bins-caid-for-rdr-alerts",
      lockAfterFirstSubmit: true,
      // Not shown at all when both ARN and BIN+CAID already exist (§11.9) - there
      // is no either/or choice to make in that state.
      showIf: { key: "payment_identifiers.both_sets", notEquals: true }
    },
    {
      key: PAYMENT_IDENTIFIER_FIELD.BIN_CODES,
      type: "text-multi",
      label: "BIN codes",
      required: true,
      placeholder: "Enter a BIN code",
      addLabel: "+ Add BIN code",
      tooltip:
        "The Bank Identification Number from your acquirer. 6 to 8 digits, starting with 3, 4, 5 or 6.",
      validateValue: validateBinCode,
      lockAfterFirstSubmit: true,
      showIf: {
        any: [
          { key: "payment_identifiers.use_bin_caid", equals: true },
          { key: "payment_identifiers.both_sets", equals: true }
        ]
      }
    },
    {
      key: PAYMENT_IDENTIFIER_FIELD.CAID_CODES,
      type: "text-multi",
      label: "CAID codes",
      required: true,
      placeholder: "Enter a CAID code",
      addLabel: "+ Add CAID code",
      tooltip:
        "The Card Acceptor ID assigned by your acquirer. 10 to 15 uppercase letters and digits.",
      validateValue: validateCaidCode,
      uppercase: true,
      lockAfterFirstSubmit: true,
      showIf: {
        any: [
          { key: "payment_identifiers.use_bin_caid", equals: true },
          { key: "payment_identifiers.both_sets", equals: true }
        ]
      }
    }
  ],
  [RECEIPT_CONFIGURE_SLUGS.support_legal]: [
    { key: "support_phone", type: "phone", label: "Support phone", required: true, max: 20 },
    {
      key: "support_email",
      type: "text",
      label: "Support email",
      required: true,
      max: 200,
      placeholder: "support@example.com"
    },
    // §11.4 forbids auto-prepending a scheme, so every URL placeholder shows one:
    // a scheme-less example would promise a format neither the field validator nor
    // the backend's URLField accepts.
    {
      key: "support_website",
      type: "url",
      label: "Support website",
      max: 500,
      optional: true,
      placeholder: "https://example.com/support"
    },
    {
      key: "terms_url",
      type: "url",
      label: "Terms and conditions",
      max: 500,
      optional: true,
      placeholder: "https://yourcompany.com/terms"
    },
    {
      key: "refund_policy_url",
      type: "url",
      label: "Refund policy",
      max: 500,
      optional: true,
      placeholder: "https://yourcompany.com/refund-policy"
    },
    {
      key: "cancellation_policy_url",
      type: "url",
      label: "Cancellation policy",
      max: 500,
      optional: true,
      placeholder: "https://yourcompany.com/cancellation"
    }
  ],
  // Review/preview step renders the receipt card + read-only summary, no fields.
  [RECEIPT_CONFIGURE_SLUGS.preview]: []
}

export default SECTION_SCHEMA
